import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '../../supabase';
import { 
  FiUsers, 
  FiSettings,
  FiBarChart2,
  FiGift,
  FiStar,
  FiLogOut,
  FiMenu,
  FiX,
  FiPackage,
  FiFileText,
  FiActivity,
  FiLayers,
  FiChevronDown,
  FiTag,
  FiUserCheck,
  FiPrinter,
  FiLogIn
} from 'react-icons/fi';
import logo from '../../assets/logo800.webp';
import packageJson from '../../../package.json';

export default function AdminDashboardNav() {
  const navigate = useNavigate();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null); // dropdown label key
  const dropdownRef = useRef(null);
  // Every other page this nav appears on is wrapped in ProtectedRoute, so a
  // session is guaranteed there - but /print has no login gate, so an
  // anonymous visitor must not see admin-only actions (logout, admin mgmt)
  // that imply a session they don't have.
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    let cancelled = false;
    supabase.auth.getUser().then(({ data }) => {
      if (!cancelled) setIsAuthenticated(!!data.user);
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (!cancelled) setIsAuthenticated(!!session?.user);
    });
    return () => { cancelled = true; subscription.unsubscribe(); };
  }, []);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Top-level nav items (always visible) - kept to just the two most-used:
  // the landing page and the physical print tool. Everything else is grouped
  // into dropdowns below instead of crowding the bar with 7+ loose items.
  const primaryNav = [
    { path: '/', icon: FiBarChart2, label: 'Oversikt' },
    { path: '/print', icon: FiPrinter, label: 'Print' },
  ];

  // Dropdown groups
  const dropdowns = [
    {
      label: 'Rapporter',
      icon: FiFileText,
      items: [
        { path: '/report', icon: FiFileText, label: 'Rapport' },
        { path: '/statistikk', icon: FiActivity, label: 'Statistikk' },
      ],
    },
    {
      label: 'Kunder',
      icon: FiUsers,
      items: [
        { path: '/user-list', icon: FiUsers, label: 'Brukere' },
        { path: '/guest-orders', icon: FiUserCheck, label: 'Gjester' },
      ],
    },
    {
      label: 'Produkter',
      icon: FiPackage,
      items: [
        { path: '/product-management', icon: FiPackage, label: 'Produkter' },
        { path: '/klippekort-oversikt', icon: FiLayers, label: 'Klippekort' },
      ],
    },
    {
      label: 'Mer',
      icon: FiGift,
      items: [
        { path: '/new-coupon', icon: FiGift, label: 'Kuponger' },
        { path: '/new-reward', icon: FiStar, label: 'Rewards' },
        { path: '/rabattkoder', icon: FiTag, label: 'Rabattkoder' },
      ],
    },
  ];

  const isActive = (path) => location.pathname === path;
  const isDropdownActive = (group) => group.items.some(i => isActive(i.path));

  const handleNavigation = (path) => {
    navigate(path);
    setIsMobileMenuOpen(false);
    setOpenDropdown(null);
  };

  return (
    <>
      <nav style={styles.navbar}>
        <div style={styles.navContainer}>
          {/* Logo/Brand */}
          <div style={styles.brand}>
            <img src={logo} alt="PWS Logo" style={styles.brandIcon} className="brand-icon" />
            <span style={styles.brandText}>Kontrollpanel</span>
            <span style={styles.brandVersion}>v{packageJson.version}</span>
          </div>

          {/* Hamburger Menu Button (Mobile Only) */}
          <button
            style={styles.hamburgerButton}
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="hamburger-button"
          >
            {isMobileMenuOpen ? (
              <FiX style={styles.hamburgerIcon} />
            ) : (
              <FiMenu style={styles.hamburgerIcon} />
            )}
          </button>

          {isAuthenticated ? (
            <>
              {/* Navigation Items (Desktop) */}
              <div style={styles.navItems} className="desktop-nav-items" ref={dropdownRef}>
                {/* Primary items */}
                {primaryNav.map((item) => {
                  const IconComponent = item.icon;
                  return (
                    <button
                      key={item.path}
                      onClick={() => handleNavigation(item.path)}
                      style={{
                        ...styles.navItem,
                        ...(isActive(item.path) ? styles.navItemActive : {})
                      }}
                      className="nav-item-hover"
                    >
                      <IconComponent style={styles.navIcon} />
                      <span style={styles.navLabel}>{item.label}</span>
                    </button>
                  );
                })}

                {/* Dropdown groups */}
                {dropdowns.map((group) => {
                  const GroupIcon = group.icon;
                  const isOpen = openDropdown === group.label;
                  const active = isDropdownActive(group);
                  return (
                    <div key={group.label} style={{ position: 'relative' }}>
                      <button
                        onClick={() => setOpenDropdown(isOpen ? null : group.label)}
                        style={{
                          ...styles.navItem,
                          ...(active || isOpen ? styles.navItemActive : {}),
                        }}
                        className="nav-item-hover"
                      >
                        <GroupIcon style={styles.navIcon} />
                        <span style={styles.navLabel}>{group.label}</span>
                        <FiChevronDown style={{ fontSize: 13, marginLeft: 2, transition: 'transform 0.2s', transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)' }} />
                      </button>

                      {isOpen && (
                        <div style={styles.dropdownMenu}>
                          {group.items.map((item) => {
                            const ItemIcon = item.icon;
                            return (
                              <button
                                key={item.path}
                                onClick={() => handleNavigation(item.path)}
                                style={{
                                  ...styles.dropdownItem,
                                  ...(isActive(item.path) ? styles.dropdownItemActive : {}),
                                }}
                                className="dropdown-item-hover"
                              >
                                <ItemIcon style={{ fontSize: 16, flexShrink: 0 }} />
                                {item.label}
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Right Side Actions */}
              <div style={styles.rightActions}>
                <button
                  onClick={() => handleNavigation('/admin-management')}
                  style={styles.actionButton}
                  title="Adminadministrasjon"
                  className="desktop-action-button"
                >
                  <FiSettings style={styles.actionIcon} />
                </button>
                <button
                  onClick={() => setShowLogoutModal(true)}
                  style={styles.logoutButton}
                  title="Logg ut"
                  className="desktop-action-button"
                >
                  <FiLogOut style={styles.actionIcon} />
                </button>
              </div>
            </>
          ) : (
            /* Logged out (only reachable here on /print, the one page with no
               login gate) - no point listing links that all just bounce to
               /admin/login, so show a single login prompt instead of the nav. */
            <div style={styles.guestLoginCard} className="desktop-nav-items">
              <span style={styles.guestLoginText}>Ikke logget inn</span>
              <button
                onClick={() => handleNavigation('/admin/login')}
                style={styles.guestLoginButton}
              >
                <FiLogIn size={15} /> Logg inn
              </button>
            </div>
          )}
        </div>

        {/* Mobile Dropdown Menu */}
        {isMobileMenuOpen && (
          <div style={styles.mobileDropdown} className="mobile-dropdown">
            <div style={styles.mobileDropdownContent}>
              {!isAuthenticated ? (
                // Logged out (only reachable on /print) - every other item
                // here just bounces to /admin/login, so skip straight to it.
                <button
                  onClick={() => handleNavigation('/admin/login')}
                  style={styles.mobileActionItem}
                  className="mobile-nav-item-hover"
                >
                  <FiLogIn style={styles.mobileNavIcon} />
                  <span style={styles.mobileNavLabel}>Logg inn</span>
                </button>
              ) : (
                <>
                  {/* Primary items */}
                  {primaryNav.map((item) => {
                    const IconComponent = item.icon;
                    return (
                      <button
                        key={item.path}
                        onClick={() => handleNavigation(item.path)}
                        style={{
                          ...styles.mobileNavItem,
                          ...(isActive(item.path) ? styles.mobileNavItemActive : {})
                        }}
                        className="mobile-nav-item-hover"
                      >
                        <IconComponent style={styles.mobileNavIcon} />
                        <span style={styles.mobileNavLabel}>{item.label}</span>
                      </button>
                    );
                  })}
                  {/* Dropdown group items (flat in mobile) */}
                  {dropdowns.flatMap(g => g.items).map((item) => {
                    const IconComponent = item.icon;
                    return (
                      <button
                        key={item.path}
                        onClick={() => handleNavigation(item.path)}
                        style={{
                          ...styles.mobileNavItem,
                          ...(isActive(item.path) ? styles.mobileNavItemActive : {})
                        }}
                        className="mobile-nav-item-hover"
                      >
                        <IconComponent style={styles.mobileNavIcon} />
                        <span style={styles.mobileNavLabel}>{item.label}</span>
                      </button>
                    );
                  })}

                  {/* Mobile Actions */}
                  <div style={styles.mobileActionsContainer}>
                    <button
                      onClick={() => handleNavigation('/admin-management')}
                      style={styles.mobileActionItem}
                      className="mobile-nav-item-hover"
                    >
                      <FiSettings style={styles.mobileNavIcon} />
                      <span style={styles.mobileNavLabel}>Adminadministrasjon</span>
                    </button>
                    <button
                      onClick={() => setShowLogoutModal(true)}
                      style={styles.mobileLogoutItem}
                      className="mobile-nav-item-hover"
                    >
                      <FiLogOut style={styles.mobileNavIcon} />
                      <span style={styles.mobileNavLabel}>Logg ut</span>
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </nav>

      {/* Logout Confirmation Modal */}
      {showLogoutModal && (
        <div style={styles.modalOverlay} onClick={() => setShowLogoutModal(false)}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <FiLogOut style={styles.modalIcon} />
              <h3 style={styles.modalTitle}>Bekreft utlogging</h3>
            </div>
            <p style={styles.modalText}>Er du sikker på at du vil logge ut?</p>
            <div style={styles.modalActions}>
              <button
                onClick={() => setShowLogoutModal(false)}
                style={styles.modalCancelButton}
                className="modal-cancel-hover"
              >
                Avbryt
              </button>
              <button
                onClick={async () => {
                  try {
                    await supabase.auth.signOut();
                    localStorage.removeItem('adminSession');
                    navigate('/admin/login');
                  } catch (error) {
                    console.error('Logout error:', error);
                    alert('Kunne ikke logge ut. Vennligst prøv igjen.');
                  }
                }}
                style={styles.modalConfirmButton}
                className="modal-confirm-hover"
              >
                Logg ut
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Custom CSS styles */}
      <style jsx="true">{`
        .nav-item-hover:hover {
          background: rgba(255, 255, 255, 0.1) !important;
          transform: translateY(-1px);
        }

        .nav-item-hover {
          transition: all 0.3s ease;
        }

        .nav-item-hover:active {
          transform: translateY(0);
        }

        .mobile-nav-item-hover:hover {
          background: rgba(255, 255, 255, 0.1) !important;
        }

        .mobile-nav-item-hover {
          transition: all 0.3s ease;
        }

        .dropdown-item-hover:hover {
          background: rgba(255, 255, 255, 0.1) !important;
          color: #fff !important;
        }

        .dropdown-item-hover {
          transition: background 0.15s ease;
        }

        .modal-cancel-hover:hover {
          background: #e2e8f0 !important;
        }

        .modal-confirm-hover:hover {
          background: #dc2626 !important;
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(239, 68, 68, 0.3);
        }

        .modal-confirm-hover:active {
          transform: translateY(0);
        }

        @keyframes modalSlideIn {
          from {
            opacity: 0;
            transform: translateY(-20px) scale(0.95);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }

        .hamburger-button {
          display: none !important;
        }

        .mobile-dropdown {
          animation: slideDown 0.3s ease-out;
        }

        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        /* Touch devices without a mouse (phones and tablets, incl. landscape
           iPads whose width can match a laptop's) always get the hamburger,
           regardless of width - width alone can't tell a 1366px iPad Pro
           apart from a 1366px laptop screen, but pointer/hover can. */
        @media (hover: none) and (pointer: coarse) {
          .hamburger-button {
            display: flex !important;
          }

          .desktop-nav-items {
            display: none !important;
          }

          .desktop-action-button {
            display: none !important;
          }
        }

        /* Switch to hamburger at 1200px (covers narrow desktop/laptop windows) */
        @media (max-width: 1200px) {
          .hamburger-button {
            display: flex !important;
          }

          .desktop-nav-items {
            display: none !important;
          }

          .desktop-action-button {
            display: none !important;
          }

          .nav-container {
            padding: 0 20px !important;
            height: 65px !important;
            justify-content: space-between !important;
          }

          .brand {
            flex: 0 0 auto !important;
          }

          .right-actions {
            flex: 0 0 auto !important;
            justify-content: flex-end !important;
          }
        }

        /* Small mobile */
        @media (max-width: 480px) {
          .brand-text {
            display: none !important;
          }

          .brand-icon {
            width: 48px !important;
            height: 48px !important;
            padding: 0 !important;
          }
        }

        /* Mobile portrait */
        @media (max-width: 480px) {
          .nav-container {
            padding: 0 16px !important;
            height: 60px !important;
          }

          .brand {
            min-width: 44px !important;
          }

          .brand-icon {
            width: 48px !important;
            height: 48px !important;
            padding: 0 !important;
          }
        }

        /* Very small screens */
        @media (max-width: 360px) {
          .nav-container {
            padding: 0 12px !important;
            height: 55px !important;
          }

          .brand {
            min-width: 40px !important;
          }

          .brand-icon {
            width: 44px !important;
            height: 44px !important;
            padding: 0 !important;
          }

          .hamburger-button {
            width: 44px !important;
            height: 44px !important;
          }
        }
      `}</style>
    </>
  );
}

const styles = {
  navbar: {
    position: 'relative',
    width: '100%',
    background: '#010f3c',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
    zIndex: 1000,
    borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
  },

  navContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '0 24px',
    height: '70px',
    maxWidth: '1400px',
    margin: '0 auto'
  },

  brand: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    color: 'white',
    fontWeight: '700',
    fontSize: '18px',
    flexShrink: 0
  },

  brandIcon: {
    width: '44px',
    height: '44px',
    padding: '4px',
    background: 'white',
    borderRadius: '12px',
    objectFit: 'contain',
    flexShrink: 0,
  },

  brandText: {
    letterSpacing: '0.5px',
    whiteSpace: 'nowrap'
  },

  brandVersion: {
    fontSize: '11px',
    fontWeight: '500',
    color: 'rgba(255, 255, 255, 0.5)',
    whiteSpace: 'nowrap'
  },

  navItems: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    overflow: 'visible',
    flex: '1',
    justifyContent: 'center',
    paddingLeft: '20px',
    paddingRight: '20px'
  },

  guestLoginCard: {
    display: 'flex',
    alignItems: 'center',
    gap: '14px',
    flex: '1',
    justifyContent: 'flex-end',
    paddingRight: '4px'
  },

  guestLoginText: {
    color: 'rgba(255, 255, 255, 0.5)',
    fontSize: '14px',
    fontWeight: '500'
  },

  guestLoginButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '9px 18px',
    background: 'rgba(255, 255, 255, 0.15)',
    border: 'none',
    borderRadius: '22px',
    color: 'white',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '600',
    whiteSpace: 'nowrap'
  },

  navItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '9px 14px',
    background: 'transparent',
    border: 'none',
    borderRadius: '22px',
    color: 'rgba(255, 255, 255, 0.9)',
    cursor: 'pointer',
    fontSize: '15px',
    fontWeight: '500',
    transition: 'all 0.3s ease',
    minWidth: 'fit-content',
    whiteSpace: 'nowrap',
    userSelect: 'none',
    WebkitTapHighlightColor: 'transparent',
    flexShrink: 0
  },

  navItemActive: {
    background: 'rgba(255, 255, 255, 0.2)',
    color: 'white',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)'
  },

  navIcon: {
    fontSize: '18px'
  },

  navLabel: {
    whiteSpace: 'nowrap'
  },

  dropdownMenu: {
    position: 'absolute',
    top: 'calc(100% + 8px)',
    left: '50%',
    transform: 'translateX(-50%)',
    background: '#010f3c',
    border: '1px solid rgba(255,255,255,0.15)',
    borderRadius: 12,
    boxShadow: '0 8px 24px rgba(0,0,0,0.3)',
    minWidth: 180,
    overflow: 'hidden',
    zIndex: 2000,
  },

  dropdownItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    width: '100%',
    padding: '11px 16px',
    background: 'transparent',
    border: 'none',
    borderBottom: '1px solid rgba(255,255,255,0.07)',
    color: 'rgba(255,255,255,0.85)',
    cursor: 'pointer',
    fontSize: 14,
    fontWeight: 500,
    textAlign: 'left',
    whiteSpace: 'nowrap',
  },

  dropdownItemActive: {
    background: 'rgba(255,255,255,0.15)',
    color: '#fff',
  },

  rightActions: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    flexShrink: 0
  },

  actionButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '42px',
    height: '42px',
    background: 'rgba(255, 255, 255, 0.15)',
    border: 'none',
    borderRadius: '12px',
    color: 'white',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    userSelect: 'none',
    WebkitTapHighlightColor: 'transparent',
    flexShrink: 0
  },

  logoutButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '42px',
    height: '42px',
    background: 'rgba(231, 76, 60, 0.8)',
    border: 'none',
    borderRadius: '12px',
    color: 'white',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    userSelect: 'none',
    WebkitTapHighlightColor: 'transparent',
    flexShrink: 0
  },

  actionIcon: {
    fontSize: '17px'
  },

  // Hamburger Menu Styles
  hamburgerButton: {
    display: 'none',
    alignItems: 'center',
    justifyContent: 'center',
    width: '48px',
    height: '48px',
    background: 'rgba(255, 255, 255, 0.2)',
    border: 'none',
    borderRadius: '14px',
    color: 'white',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    userSelect: 'none',
    WebkitTapHighlightColor: 'transparent',
    marginLeft: 'auto',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
  },

  hamburgerIcon: {
    fontSize: '22px'
  },

  // Mobile Dropdown Styles
  mobileDropdown: {
    position: 'absolute',
    top: '100%',
    left: 0,
    right: 0,
    background: '#010f3c',
    borderTop: '1px solid rgba(255, 255, 255, 0.1)',
    zIndex: 999,
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
  },

  mobileDropdownContent: {
    padding: '20px 15px',
    display: 'flex',
    flexDirection: 'column',
    gap: '8px'
  },

  mobileNavItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '12px 16px',
    background: 'transparent',
    border: 'none',
    borderRadius: '12px',
    color: 'rgba(255, 255, 255, 0.9)',
    cursor: 'pointer',
    fontSize: '16px',
    fontWeight: '500',
    transition: 'all 0.3s ease',
    textAlign: 'left',
    width: '100%',
    userSelect: 'none',
    WebkitTapHighlightColor: 'transparent'
  },

  mobileNavItemActive: {
    background: 'rgba(255, 255, 255, 0.2)',
    color: 'white'
  },

  mobileNavIcon: {
    fontSize: '18px',
    minWidth: '18px'
  },

  mobileNavLabel: {
    fontSize: '16px',
    fontWeight: '500'
  },

  mobileActionsContainer: {
    marginTop: '8px',
    paddingTop: '16px',
    borderTop: '1px solid rgba(255, 255, 255, 0.1)',
    display: 'flex',
    flexDirection: 'column',
    gap: '8px'
  },

  mobileActionItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '12px 16px',
    background: 'transparent',
    border: 'none',
    borderRadius: '12px',
    color: 'rgba(255, 255, 255, 0.8)',
    cursor: 'pointer',
    fontSize: '16px',
    fontWeight: '500',
    transition: 'all 0.3s ease',
    textAlign: 'left',
    width: '100%',
    userSelect: 'none',
    WebkitTapHighlightColor: 'transparent'
  },

  mobileLogoutItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '12px 16px',
    background: 'rgba(231, 76, 60, 0.2)',
    border: '1px solid rgba(231, 76, 60, 0.3)',
    borderRadius: '12px',
    color: 'rgba(231, 76, 60, 1)',
    cursor: 'pointer',
    fontSize: '16px',
    fontWeight: '600',
    transition: 'all 0.3s ease',
    textAlign: 'left',
    width: '100%',
    userSelect: 'none',
    WebkitTapHighlightColor: 'transparent'
  },

  // Logout Modal Styles
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 9999,
    backdropFilter: 'blur(4px)'
  },

  modalContent: {
    background: 'white',
    borderRadius: '16px',
    padding: '32px',
    maxWidth: '420px',
    width: '90%',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
    animation: 'modalSlideIn 0.3s ease-out'
  },

  modalHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '16px'
  },

  modalIcon: {
    fontSize: '28px',
    color: '#ef4444',
    background: '#fee2e2',
    padding: '12px',
    borderRadius: '12px'
  },

  modalTitle: {
    fontSize: '22px',
    fontWeight: '600',
    color: '#010f3c',
    margin: 0
  },

  modalText: {
    fontSize: '15px',
    color: '#64748b',
    marginBottom: '24px',
    lineHeight: '1.6'
  },

  modalActions: {
    display: 'flex',
    gap: '12px',
    justifyContent: 'flex-end'
  },

  modalCancelButton: {
    padding: '12px 24px',
    background: '#f1f5f9',
    border: 'none',
    borderRadius: '10px',
    color: '#64748b',
    fontSize: '15px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    fontFamily: 'Poppins, sans-serif'
  },

  modalConfirmButton: {
    padding: '12px 24px',
    background: '#ef4444',
    border: 'none',
    borderRadius: '10px',
    color: 'white',
    fontSize: '15px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    fontFamily: 'Poppins, sans-serif'
  }
};
